import React, { useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest, { shippingAccessScope } from '../../hooks/useShippingRequest';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { useT } from '../../i18n/LanguageContext';
import {
  ACCOUNTING_METHODS,
  ACCOUNTING_READINESS,
  emptyAccountingConfiguration,
  validAccountingConfiguration,
  readAccountingSettings,
  sameAccountingConfiguration,
} from './accountingSettingsReceipt';
import DistributorAccountingFields from './DistributorAccountingFields';
import DistributorAccountingStatus from './DistributorAccountingStatus';

const unknown =
  'Accounting settings could not be confirmed. Your edits are still here. Discard and reload before saving again.';
export default function DistributorAccountingSettings({
  distributor,
  disabled = false,
  onLockChange,
}) {
  const t = useT();
  const id = distributor._id;
  const [expanded, setExpanded] = useState(false);
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const pending = useRef(false);
  const session = useRef(captureSession());
  const scope = useRef(shippingAccessScope());
  const { begin } = useShippingRequest(id);
  const current = () =>
    isSessionCurrent(session.current) && scope.current === shippingAccessScope();
  const dirty = Boolean(
    saved &&
      draft &&
      !sameAccountingConfiguration(draft, saved.configuration || emptyAccountingConfiguration())
  );
  useEffect(() => {
    onLockChange?.(busy || dirty || uncertain);
    const warn = (event) => {
      if (busy || dirty || uncertain) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      onLockChange?.(false);
      window.removeEventListener('beforeunload', warn);
    };
  }, [busy, dirty, uncertain, onLockChange]);
  const apply = (value) => {
    setSaved(value);
    setDraft(value.configuration || emptyAccountingConfiguration());
    setUncertain(false);
  };
  const reload = async () => {
    if (!current() || pending.current || disabled || distributor.isActive !== true) return;
    const request = begin();
    pending.current = true;
    setBusy(true);
    setExpanded(true);
    setError('');
    setNotice('');
    try {
      const value = await distributorService.adminAccountingSettings(id, {
        signal: request.signal,
      });
      if (request.isCurrent() && current()) apply(readAccountingSettings(value, id));
    } catch {
      if (request.isCurrent() && current())
        setError('Accounting settings could not load. Please retry.');
    } finally {
      pending.current = false;
      if (request.isCurrent() && current()) setBusy(false);
    }
  };
  const save = async (configuration) => {
    if (
      !current() ||
      disabled ||
      pending.current ||
      uncertain ||
      !saved ||
      distributor.isActive !== true
    )
      return;
    if (
      configuration !== null &&
      (!dirty || !validAccountingConfiguration(configuration, saved.families))
    ) {
      setError(
        'Complete the buyer, company, family, deposit, tax and clearance-date mappings before saving.'
      );
      return;
    }
    const expectedRevision = saved.revision;
    const request = begin();
    pending.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    const accepted = (value) =>
      value.revision === expectedRevision + 1 &&
      sameAccountingConfiguration(value.configuration, configuration);
    try {
      const response = await distributorService.saveAdminAccountingSettings(id, {
        revision: expectedRevision,
        configuration,
      });
      if (!request.isCurrent() || !current()) return;
      const value = readAccountingSettings(response, id);
      if (!accepted(value)) throw new Error('Unconfirmed accounting configuration');
      apply(value);
      setNotice(
        configuration === null
          ? 'Draft accounting mapping cleared.'
          : 'Accounting mapping saved as a draft. Live activation remains off.'
      );
    } catch (failure) {
      if (!request.isCurrent() || !current()) return;
      setUncertain(true);
      if (failure?.response?.status === 409)
        setError(
          'Accounting settings changed. Your edits are still here. Discard and reload before saving again.'
        );
      else {
        try {
          const response = await distributorService.adminAccountingSettings(id, {
            signal: request.signal,
          });
          if (!request.isCurrent() || !current()) return;
          const value = readAccountingSettings(response, id);
          if (!accepted(value)) throw new Error('Unconfirmed accounting configuration');
          apply(value);
          setNotice(
            configuration === null
              ? 'Draft accounting mapping cleared.'
              : 'Accounting mapping saved as a draft. Live activation remains off.'
          );
        } catch {
          if (request.isCurrent() && current()) setError(unknown);
        }
      }
    } finally {
      pending.current = false;
      if (request.isCurrent() && current()) setBusy(false);
    }
  };
  const edit = (change) => {
    if (!current() || pending.current || disabled || uncertain || distributor.isActive !== true)
      return;
    setDraft((value) => change(value));
    setError('');
    setNotice('');
  };
  return (
    <section
      aria-label={t('Distributor accounting settings')}
      className="me-distributor-admin-card space-y-4"
    >
      <h2 className="text-lg font-semibold">
        {t('Accounting settings for {name}', { name: distributor.name })}
      </h2>
      <p>
        {t(
          'Map the exact distributor buyer and approved QuickBooks accounts. The pricing account does not identify the buyer.'
        )}
      </p>
      <p>
        {t(
          'These draft mappings do not activate payments, post accounting entries or add bank units.'
        )}
      </p>
      {distributor.isActive !== true && (
        <p role="status">{t('This distributor is inactive. Accounting settings are read-only.')}</p>
      )}
      {!expanded && (
        <button
          type="button"
          className="me-distributor-admin-secondary"
          disabled={disabled || distributor.isActive !== true}
          onClick={reload}
        >
          {t('Configure accounting')}
        </button>
      )}
      {error && (
        <p role="alert" className="text-red-700">
          {t(error)}
        </p>
      )}
      {notice && <p role="status">{t(notice)}</p>}
      {busy && <p role="status">{t('Checking accounting settings...')}</p>}
      {expanded && !draft && !busy && (
        <button type="button" disabled={disabled} onClick={reload}>
          {t('Retry')}
        </button>
      )}
      {saved && (
        <>
          <p role="status">{t(ACCOUNTING_READINESS[saved.readiness.code])}</p>
          <p>
            {t('Accounting treatment')}: {t('Sale on verified payment clearance')} · {t('Currency')}
            : USD
          </p>
        </>
      )}
      {draft && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            save(draft);
          }}
          className="space-y-4"
        >
          <DistributorAccountingFields
            draft={draft}
            families={saved.families}
            edit={edit}
            disabled={disabled || busy || uncertain || distributor.isActive !== true}
            methods={ACCOUNTING_METHODS}
          />
          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              className="me-distributor-admin-primary"
              disabled={
                disabled ||
                busy ||
                uncertain ||
                !dirty ||
                !validAccountingConfiguration(draft, saved.families) ||
                distributor.isActive !== true
              }
            >
              {t('Save draft accounting mapping')}
            </button>
            {(dirty || uncertain) && (
              <button
                type="button"
                className="me-distributor-admin-secondary"
                disabled={disabled || busy || distributor.isActive !== true}
                onClick={reload}
              >
                {t('Discard accounting edits and reload')}
              </button>
            )}
            {saved.configuration && !dirty && (
              <button
                type="button"
                className="me-distributor-admin-secondary"
                disabled={disabled || busy || uncertain || distributor.isActive !== true}
                onClick={() => save(null)}
              >
                {t('Clear draft accounting mapping')}
              </button>
            )}
          </div>
        </form>
      )}
      <DistributorAccountingStatus key={id} distributorId={id} />
    </section>
  );
}
